import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subscription } from 'rxjs';

import { BaseService, ConfigService, IUtilsDictionary, LoggingService, PITEventService, UtilsDictionary, UtilsMisc, UtilsString } from '@pit/core';
import { IPinSignalRClientService, SignalRConnectionState } from './pin.signalr.client.service.interface';
import { ISignalRRetryPolicy } from './signalr.retry.policy.interface';

@Injectable({
  providedIn: 'root'
})
export class PinSignalRClientService extends BaseService implements IPinSignalRClientService {

  private isRestartTryAllowed: boolean = false;
  protected _connection: signalR.HubConnection = null;
  protected _connectionState: SignalRConnectionState = SignalRConnectionState.Closed;
  protected _isConnected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected _listeners: IUtilsDictionary<(message: any) => void> = null;

  constructor(configurationService: ConfigService, loggingService: LoggingService, private eventService: PITEventService) {
    super(configurationService, loggingService);

    this.loggingService.debug('PinSignalRClientService --> constructor()');
  }

  public accessTokenFactory: string | Promise<string> = null;

  public get connectionId(): string {
    return ((this.connectionState === SignalRConnectionState.Started) ? this.connection.connectionId : null);
  }

  public get connectionState(): SignalRConnectionState {
    return this._connectionState;
  }

  public get isConnected(): boolean {
    return this.isConnected$.getValue();
  }

  public get url(): string {
    return `/${this.configurationService.configCommChannel.urlChannel}`
  }

  public async closeConnectionAsync(): Promise<void> {
    try {
      this.isRestartTryAllowed = false;
      this.setConnectionState(SignalRConnectionState.CloseRequest);
      if (this.connection.state === signalR.HubConnectionState.Connected) {
        await this.connection.stop();
      }
      this.setConnectionState(SignalRConnectionState.Closed);
      //this._connection = null;
    } catch (error) {
      this.eventService.useMethod('error.log', error);
    }
  }

  public async startConnectionAsync(): Promise<void> {
    try {
      this.setConnectionState(SignalRConnectionState.StartRequest);
      if (this.connection.state !== signalR.HubConnectionState.Connected) {
        this.isRestartTryAllowed = true;
        this.connection.start()
          .then(async () => await this.doOnStartedAsync())
          .catch((error) => {
            this.eventService.useMethod('error.log', error);
            if (this.isRestartTryAllowed === true) {
              setTimeout(async () => {
                if (this.isRestartTryAllowed === true) {
                  await this.startConnectionAsync();
                }
              }, 1000 * 60);
            }
          });
      } else {
        this.setConnectionState(SignalRConnectionState.Started);
      }
    } catch (error) {
      this.eventService.useMethod('error.log', error);
    }
  }

  public addListener(messageName: string, callback: (arg: any) => void): void {
    this.listeners.set(messageName, callback);
    if (this._connection !== null) {
      this.connection.on(messageName, callback);
    }
  }

  public clearListeners(): void {
    if (this._connection !== null) {
      this.listeners.keys.forEach(key => {
        try {
          this.connection.off(key);
        } catch (error) {
          this.eventService.useMethod('error.log', error);
        }
      });
    }
    this.listeners.clear();
  }

  public async sendMessageAsync(methodName: string, ...args: any[]): Promise<any> {
    let result: any = null;

    try {
      if ((UtilsMisc.isNullOrUndefined(args) === false) && (args.length > 0)) {
        switch (args.length) {
          case 1: {
            result = await this.connection.invoke(methodName, args[0]);
            break;
          }
          case 2: {
            result = await this.connection.invoke(methodName, args[0], args[1]);
            break;
          }
          case 3: {
            result = await this.connection.invoke(methodName, args[0], args[1], args[2]);
            break;
          }
          default: {
            result = await this.connection.invoke(methodName, args);
            break;
          }
        }
      } else {
        result = await this.connection.invoke(methodName);
      }
    } catch (error) {
      this.eventService.useMethod('error.log', error);
    }

    return result;
  }

  public setRetryPolicy(policy: ISignalRRetryPolicy): void {
    if (UtilsMisc.isNullOrUndefined(this.retryPolicy) === true) {
      this.retryPolicy = policy;
    }
  }

  public subscribeForConnectionChange(method: (value: boolean, connectionId: string) => void): Subscription {
    return this.isConnected$.subscribe(value => method(value, this.connectionId));
  }

  protected retryPolicy: ISignalRRetryPolicy = null;

  protected get connection(): signalR.HubConnection {
    if (this._connection === null) {
      this._connection = this.doCreateConnection();
    }

    return this._connection;
  }

  protected get isConnected$(): BehaviorSubject<boolean> {
    return this._isConnected$;
  }

  protected get listeners(): IUtilsDictionary<(message: any) => void> {
    if (this._listeners === null) {
      this._listeners = new UtilsDictionary<(message: any) => void>();
    }

    return this._listeners;
  }

  protected doCreateConnection(): signalR.HubConnection {
    if (this._connection === null) {
      /*const options: signalR.IHttpConnectionOptions = {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets
      };
      if (UtilsMisc.isNullOrUndefined(this.accessTokenFactory) === false) {
          options.accessTokenFactory = () => this.accessTokenFactory;
      }*/
      /*const options: signalR.IHttpConnectionOptions = {};
      if ((this.configurationService.configAuth.useAuth === true) &&
          (UtilsString.stringIsNullOrEmpty(this.configurationService.configAuth.authUserName) === false) &&
          (UtilsString.stringIsNullOrEmpty(this.configurationService.configAuth.authPassword) === false)) {
          options.headers = {
              'Authorization': 'Basic ' + btoa(UtilsString.stringFormat('{0}:{1}', this.configurationService.configAuth.authUserName, this.configurationService.configAuth.authPassword))
          };
      }*/

      let hubConnectionBuilder: signalR.HubConnectionBuilder = new signalR.HubConnectionBuilder()
        //.withUrl(this.url, options)
        .withUrl(this.url)
        .configureLogging(signalR.LogLevel.Information);
      if (UtilsMisc.isNullOrUndefined(this.retryPolicy) === false) {
        hubConnectionBuilder = hubConnectionBuilder.withAutomaticReconnect(this.retryPolicy);
      } else {
        hubConnectionBuilder = hubConnectionBuilder.withAutomaticReconnect();
      }
      this._connection = hubConnectionBuilder.build();

      //this.connection.serverTimeoutInMilliseconds = 3000;

      this.connection.onreconnecting(() => {
        this.setConnectionState(SignalRConnectionState.Reconnecting);
      });

      this.connection.onreconnected(async () => {
        await this.doOnReconnectedAsync();
      });

      this._connection.onclose(() => {
        this.setConnectionState(SignalRConnectionState.Closed);
      });

      if (UtilsMisc.isNullOrUndefined(this._listeners) === false) {
        this.listeners.keys.forEach(key => {
          this.connection.on(key, this.listeners.getValue(key));
        });
      }
    }

    return this._connection;
  }

  protected async doOnStartedAsync(): Promise<void> {
    this.isRestartTryAllowed = false;
    this.setConnectionState(SignalRConnectionState.Started);
  }

  protected async doOnReconnectedAsync(): Promise<void> {
    this.setConnectionState(SignalRConnectionState.Started);
  }

  protected setConnectionState(value: SignalRConnectionState): void {
    if (value !== this.connectionState) {
      this._connectionState = value;
      this.isConnected$.next(this.connectionState === SignalRConnectionState.Started);
      this.eventService.sendEvent('network', {
        type: 'signalr',
        state: (this.connectionState === SignalRConnectionState.Started)
      })
    }
  }

}
